import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Grid,
  InputGroup,
  Dialog,
  Heading,
  majorScale,
  FormField,
  TextArea,
  defaultColors,
  Button,
  TagInput,
  NumberInput,
  ControlGroup,
  EmptyInputGroup,
  HTMLSelect,
  minorScale,
  defaultFills,
  useToast,
} from '../../npaper';
import { AddressFormControls } from '../../npaper/AddressFormControls';
import { CurrencySelect } from '../../npaper/CurrencySelect';
import { UsersInput } from '../../npaper/UserSelect';
import { useStores } from '../../stores';
import {
  formatPaymentLikelihoodType,
  ICompany,
  ICompanySnapshotIn,
  PaymentLikelihoodType,
  PaymentTermsType,
} from '../../stores/contacts/CompanyStore';
import validateEmail from '../../utils/email-validator';

export interface ICompanyDetailsDialogProps {
  isShown: boolean;
  onClose: (company?: ICompany) => void;
  company?: ICompany;
}

export const CompanyDetailsDialog: React.FC<ICompanyDetailsDialogProps> = ({
  company,
  ...props
}) => {
  const [isShown, setIsShown] = useState(props.isShown);
  const [created, setCreated] = useState<ICompany>();
  useEffect(() => {
    setIsShown(props.isShown);
  }, [props.isShown]);

  const close = () => {
    setIsShown(false);
  };
  const toaster = useToast();
  const intl = useIntl();
  const { companyStore, authStore, organisationStore } = useStores();

  const formProps = useForm<ICompanySnapshotIn>();
  const {
    register,
    reset,
    control,
    errors,
    handleSubmit,
    formState,
    watch,
    setError,
    clearErrors,
    setValue,
    getValues,
  } = formProps;

  useEffect(() => {
    if (!props.isShown) return;
    if (company) {
      reset(getSnapshot(company));
    } else {
      reset({});
    }
    setVatValid(null);
    setValidatingVat(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, props.isShown]);

  const onSubmit = async (values: ICompanySnapshotIn) => {
    if (company) {
      // Update company
      try {
        await companyStore.updateItem(company, values);
        close();
      } catch (err) {
        handleError(err);
      }
    } else {
      try {
        const company = await companyStore.createItem(values);
        organisationStore.getItem(authStore.currentOrganisation?.id, true);
        setCreated(company);
        close();
      } catch (err) {
        // TODO: Handle error
        handleError(err);
      }
    }
  };

  const handleError = (error: any) => {
    if (error.response && error.response.data) {
      const data = error.response.data;
      if (
        data['non_field_errors'] &&
        data['non_field_errors'].length > 0 &&
        data['non_field_errors'].reduce(
          (p: boolean, n: string) => p || n.indexOf('vat_number'),
          false
        )
      ) {
        // This might not be true - but we do it anyways
        setError('vat_number', {
          message: intl.formatMessage({
            defaultMessage:
              'There already exist a company with that VAT number',
          }),
          type: 'unique',
        });
      } else if (data['company_number'] && data['company_number'].length > 0) {
        setError('company_number', {
          message: data['company_number'].join('. '),
        });
      } else {
        toaster.showDefaultError(error);
      }
      return;
    }
    toaster.showDefaultError(error);
  };

  const [vatValid, setVatValid] = useState<boolean | null>(null);
  const [validatingVat, setValidatingVat] = useState(false);
  const vatNumber = watch('vat_number');

  const validateVat = React.useCallback(() => {
    if (!vatNumber || vatNumber.length <= 2) {
      return;
    }
    setValidatingVat(true);
    companyStore
      .validateVatNumber(vatNumber)
      .then((res) => {
        setValidatingVat(false);
        setVatValid(res.valid);
        if (res.valid) {
          clearErrors('vat_number');
        }
        if (res.valid) {
          const values = getValues();
          setValue('name', values.name || res.name);
          setValue('name', values.name || res.name);
          setValue('address.country', res.country);
          setValue(
            'address.address_line1',
            (values.address && values.address.address_line1) || res.address
          );
          setValue(
            'address.postal_code',
            (values.address && values.address.postal_code) || res.postal_code
          );
          setValue(
            'address.city',
            (values.address && values.address.city) || res.city
          );
        }
      })
      .catch((err) => {
        setValidatingVat(false);
        setVatValid(false);
        if (
          err.response &&
          err.response.data &&
          err.response.data['vat_number']
        ) {
          const errorString = err.response.data['vat_number'];
          if (errorString === 'INVALID_INPUT') {
            setError('vat_number', {
              message: intl.formatMessage({
                defaultMessage:
                  'Invalid input for VAT number. Please ensure you have the prepended country code, e.g. DK12345678.',
              }),
            });
          }
        }
      });
  }, [
    clearErrors,
    companyStore,
    getValues,
    intl,
    setError,
    setValue,
    vatNumber,
  ]);

  return (
    <Dialog
      title={company ? 'Rediger virksomhed' : 'Opret virksomhed'}
      isShown={isShown}
      onClose={() => props.onClose(created)}
      actions={
        <>
          <Button onClick={() => close()} disabled={formState.isSubmitting}>
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            isLoading={formState.isSubmitting}
          >
            {company
              ? intl.formatMessage({ defaultMessage: 'Save changes' })
              : intl.formatMessage({ defaultMessage: 'Create company' })}
          </Button>
        </>
      }
    >
      <FormProvider {...formProps}>
        <Grid padding={majorScale(2)}>
          <Heading size={300} className="col-span-12">
            <FormattedMessage defaultMessage="Details" />
          </Heading>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'VAT number' })}
            inline={true}
            isInvalid={!!errors.vat_number}
            hint={
              errors.vat_number
                ? errors.vat_number.message ||
                  intl.formatMessage({
                    defaultMessage:
                      'Please prepend the VAT number with a country code and use numbers only, e.g. DK12345678.',
                  })
                : intl.formatMessage({
                    defaultMessage:
                      'With a VAT number, we can automatically fetch most informations about the company.',
                  })
            }
          >
            <InputGroup
              placeholder={intl.formatMessage({
                defaultMessage: "The company's VAT or organisational number",
              })}
              isInvalid={!!errors.vat_number}
              name="vat_number"
              ref={register({
                pattern:
                  /^[A-Za-z]{2,4}(?=.{2,12}$)[-_\s0-9]*(?:[a-zA-Z][-_\s0-9]*){0,2}$/,
              })}
              onBlur={() => validateVat()}
              isLoading={validatingVat}
              iconRight={
                vatValid ? 'tick' : vatValid === false ? 'error' : undefined
              }
              iconColor={
                vatValid ? defaultFills.success[500] : defaultFills.danger[500]
              }
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Name' })}
            inline={true}
          >
            <InputGroup
              placeholder={intl.formatMessage({
                defaultMessage: 'Company name',
              })}
              name="name"
              ref={register({
                required: intl.formatMessage({
                  defaultMessage: "Please enter the company's name",
                }),
              })}
              isInvalid={!!errors.name}
              validationMessage={errors.name && errors.name.message}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({
              defaultMessage: 'Contact information',
            })}
            inline={true}
          >
            <InputGroup
              placeholder={intl.formatMessage({ defaultMessage: 'Email' })}
              name="email"
              ref={register({
                validate: (v: string) =>
                  (!validateEmail(v) &&
                    intl.formatMessage({
                      defaultMessage: 'Please provide a valid email address',
                    })) ||
                  true,
              })}
              onBlur={(e) => {
                const email = e.target.value;
                const { domains, default_att } = getValues();
                if (
                  !errors.email &&
                  email.split('@').length > 1 &&
                  domains &&
                  domains.length === 0
                ) {
                  setValue('domains', [{ domain: email.split('@').pop() }]);
                }
                if (!default_att) {
                  setValue('default_att', email);
                }
              }}
            />
            <InputGroup
              placeholder={intl.formatMessage({ defaultMessage: 'Phone' })}
              name="phone"
              ref={register}
            />
            <Controller
              control={control}
              name="domains"
              defaultValue={[]}
              render={({ onChange, onBlur, value }) => (
                <TagInput
                  marginBottom={minorScale(1)}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Add a domain...',
                    description: 'Domain select placeholder',
                  })}
                  value={value.map((d: { domain: string }) => d.domain)}
                  onChange={(tags) =>
                    onChange(
                      tags.map((t) => ({
                        domain: t.trim().split('www.').pop(),
                      }))
                    )
                  }
                  onBlur={onBlur}
                />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Address' })}
            inline={true}
          >
            <AddressFormControls name="address" />
          </FormField>
        </Grid>

        <Grid
          borderTop={`2px solid ${defaultColors.gray[100]}`}
          padding={majorScale(2)}
        >
          <Heading size={300} className="col-span-12">
            <FormattedMessage defaultMessage="Payment information" />
          </Heading>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Payment likelihood' })}
            inline={true}
            hint={
              company && company.payment_likelihood
                ? company.payment_likelihood.updated_by
                  ? intl.formatMessage(
                      { defaultMessage: 'Last changed {date} by {name}' },
                      {
                        date: intl.formatDate(
                          company.payment_likelihood.updated_at,
                          { format: 'default' }
                        ),
                        name: company.payment_likelihood.updated_by?.user.name,
                      }
                    )
                  : intl.formatMessage(
                      { defaultMessage: 'Last changed {date}' },
                      {
                        date: intl.formatDate(
                          company.payment_likelihood.updated_at,
                          { format: 'default' }
                        ),
                      }
                    )
                : undefined
            }
          >
            <Controller
              control={control}
              name="payment_likelihood.type"
              defaultValue={PaymentLikelihoodType.HIGH}
              render={({ onChange, value }) => (
                <HTMLSelect
                  value={value}
                  onChange={onChange}
                  options={Object.values(PaymentLikelihoodType).map((v) => ({
                    label: formatPaymentLikelihoodType(intl, v),
                    value: v,
                  }))}
                />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Payment terms' })}
            inline={true}
          >
            <ControlGroup>
              <Controller
                control={control}
                name="payment_terms_type"
                defaultValue={PaymentTermsType.NET}
                rule={{
                  required: intl.formatMessage({
                    defaultMessage: 'Choose payment terms',
                  }),
                }}
                render={({ onChange, value }) => (
                  <HTMLSelect
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Choose type',
                    })}
                    flexGrow={1}
                    options={[
                      {
                        label: intl.formatMessage({ defaultMessage: 'Net' }),
                        value: PaymentTermsType.NET,
                      },
                      {
                        label: intl.formatMessage({
                          defaultMessage: 'Net cash',
                        }),
                        value: PaymentTermsType.NET_CASH,
                      },
                      {
                        label: intl.formatMessage({
                          defaultMessage: 'Invoice month',
                        }),
                        value: PaymentTermsType.INVOICE_MONTH,
                      },
                      {
                        label: intl.formatMessage({
                          defaultMessage: 'Prepaid',
                        }),
                        value: PaymentTermsType.PREPAID,
                      },
                      {
                        label: intl.formatMessage({ defaultMessage: 'Custom' }),
                        value: PaymentTermsType.CUSTOM,
                      },
                    ]}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="days_of_credit"
                defaultValue={8}
                render={({ onChange, value, onBlur }) => (
                  <NumberInput
                    width={60}
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                    onChange={onChange}
                    value={value}
                    onBlur={onBlur}
                  />
                )}
              />
              <EmptyInputGroup>
                <FormattedMessage defaultMessage="days" />
              </EmptyInputGroup>
            </ControlGroup>
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Currency' })}
            inline={true}
          >
            <Controller
              control={control}
              name="currency"
              defaultValue={authStore.currentOrganisation?.default_currency}
              render={({ onChange, value }) => (
                <CurrencySelect value={value} onChange={onChange} />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Invoicing email' })}
            inline={true}
          >
            <InputGroup
              placeholder={intl.formatMessage({ defaultMessage: 'Email' })}
              name="default_att"
              ref={register}
            />
          </FormField>

          {/*TODO: Consider if we need this: <FormField
            className="col-span-9"
            label="Tilbud og faktura"
            inline={true}
          >
            <Controller
              control={control}
              name="add_cc_on_offer"
              render={({ onChange, value }) => (
                <Checkbox
                  label="Sæt kontaktperson CC på tilbud"
                  {...{ onChange, value }}
                />
              )}
            />
            <Controller
              control={control}
              name="add_cc_on_invoice"
              render={({ onChange, value }) => (
                <Checkbox
                  label="Sæt kontaktperson CC på faktura"
                  {...{ onChange, value }}
                />
              )}
            />
          </FormField>*/}
        </Grid>

        <Grid
          borderTop={`2px solid ${defaultColors.gray[100]}`}
          padding={majorScale(2)}
        >
          <Heading size={300} className="col-span-12">
            <FormattedMessage defaultMessage="Administration" />
          </Heading>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Company number' })}
            inline
          >
            <InputGroup
              type="number"
              textAlign="left"
              name="company_number"
              formNoValidate
              autoComplete="off"
              ref={register({
                required:
                  company &&
                  intl.formatMessage({
                    defaultMessage: 'You must provide a company number',
                  }),
                valueAsNumber: true,
                validate: (v) =>
                  !v ||
                  !isNaN(parseFloat(v)) ||
                  intl.formatMessage({
                    defaultMessage: 'Please provide a positive integer',
                  }),
              })}
              isInvalid={!!errors.company_number}
              validationMessage={errors.company_number?.message}
              placeholder={`${
                authStore.currentOrganisation!.next_company_number
              }`}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Reference' })}
            inline={true}
          >
            <InputGroup
              placeholder={intl.formatMessage({
                defaultMessage: 'A reference',
              })}
              name="reference"
              ref={register}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Description' })}
            inline={true}
          >
            <TextArea
              placeholder={intl.formatMessage({
                defaultMessage: 'A short description of the company',
              })}
              name="description"
              ref={register}
              maxLength={500}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Responsible' })}
            inline={true}
            hint={intl.formatMessage({
              defaultMessage:
                'Choose a person from your team to be responsible for the company',
            })}
          >
            <Controller
              control={control}
              name="responsible"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <UsersInput
                  onBlur={onBlur}
                  value={value}
                  onChange={(users) => {
                    onChange(users.map((c) => c.id));
                  }}
                />
              )}
            />
          </FormField>

          <FormField
            className="col-span-9"
            label={intl.formatMessage({ defaultMessage: 'Tags' })}
            inline
          >
            <Controller
              control={control}
              name="tags"
              defaultValue={[]}
              render={({ onChange, value, onBlur }) => (
                <TagInput value={value} onChange={onChange} onBlur={onBlur} />
              )}
            />
          </FormField>
        </Grid>
      </FormProvider>
    </Dialog>
  );
};
